import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useAppState } from './AppState.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import { useInvalidateQueries, useRequiredLeagueId, useSyncLeagueFromRoute } from './hooks.js';
import { CourseScorecardGrid, normalizeHoleValues, sumHolePars } from '../lib/courseScorecard.js';

const DEFAULT_PARS = normalizeHoleValues([4, 3, 5, 4, 4, 3, 5, 4, 4], 4);
const DEFAULT_HANDICAPS = normalizeHoleValues([1, 2, 3, 4, 5, 6, 7, 8, 9], 1);

export function AdminCoursesPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const coursesQuery = useQuery({
    queryKey: ['courses', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getCourses(leagueId)).courses,
  });

  const [draft, setDraft] = useState({
    name: '',
    courseRating: '35.5',
    slopeRating: '113',
    holePars: [...DEFAULT_PARS],
    holeHandicaps: [...DEFAULT_HANDICAPS],
  });

  const createMutation = useMutation({
    mutationFn: async () =>
      client!.createCourse(leagueId, {
        name: draft.name.trim(),
        par: sumHolePars(draft.holePars),
        courseRating: Number(draft.courseRating || 35.5),
        slopeRating: Number(draft.slopeRating || 113),
        holePars: draft.holePars,
        holeHandicaps: draft.holeHandicaps,
      }),
    onSuccess: async () => {
      setBanner('success', 'Course created.');
      setDraft({
        name: '',
        courseRating: '35.5',
        slopeRating: '113',
        holePars: [...DEFAULT_PARS],
        holeHandicaps: [...DEFAULT_HANDICAPS],
      });
      await invalidate(['courses', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  if (coursesQuery.isLoading) {
    return <LoadingPanel title="Courses" message="Loading course metadata." />;
  }
  if (coursesQuery.isError || !coursesQuery.data) {
    return <ErrorPanel title="Courses" message={mapError(coursesQuery.error)} />;
  }

  return (
    <>
      <PageIntro
        title="Courses"
      />
      <section className="grid courses-admin-layout">
        <article className="panel course-create-panel">
          <PanelHeader title="Create Course" />
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void createMutation.mutateAsync();
            }}
          >
            <div className="row">
              <div className="field">
                <label>Name</label>
                <input required value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
              </div>
              <div className="field">
                <label>Total Par</label>
                <div className="scorecard-total">{sumHolePars(draft.holePars)}</div>
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>Course Rating</label>
                <input type="number" step="0.1" value={draft.courseRating} onChange={(event) => setDraft((current) => ({ ...current, courseRating: event.target.value }))} />
              </div>
              <div className="field">
                <label>Slope Rating</label>
                <input type="number" min="55" max="155" value={draft.slopeRating} onChange={(event) => setDraft((current) => ({ ...current, slopeRating: event.target.value }))} />
              </div>
            </div>
            <div className="field">
              <label>Scorecard</label>
              <CourseScorecardGrid
                holePars={draft.holePars}
                holeHandicaps={draft.holeHandicaps}
                onParChange={(holeIndex, value) =>
                  setDraft((current) => ({
                    ...current,
                    holePars: current.holePars.map((par, index) => (index === holeIndex ? value : par)),
                  }))
                }
                onHandicapChange={(holeIndex, value) =>
                  setDraft((current) => ({
                    ...current,
                    holeHandicaps: current.holeHandicaps.map((handicap, index) =>
                      index === holeIndex ? value : handicap,
                    ),
                  }))
                }
              />
            </div>
            <div className="inline-actions">
              <button type="submit" disabled={createMutation.isPending}>
                Create Course
              </button>
            </div>
          </form>
        </article>

        <article className="panel course-list-panel">
          <PanelHeader title={`Courses (${coursesQuery.data.length})`} />
          {coursesQuery.data.length === 0 ? (
            <EmptyState title="No courses defined" message="Create the first course before scheduling match days." />
          ) : (
            <div className="card-list">
              {coursesQuery.data.map((course) => (
                <CourseCard key={course.id} leagueId={leagueId} course={course} />
              ))}
            </div>
          )}
        </article>
      </section>
    </>
  );
}

function CourseCard({
  leagueId,
  course,
}: {
  leagueId: string;
  course: {
    id: string;
    name: string;
    course_rating: number;
    slope_rating: number;
    hole_pars: number[];
    hole_handicaps: number[];
  };
}) {
  const { client, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const [form, setForm] = useState({
    name: course.name,
    courseRating: String(course.course_rating),
    slopeRating: String(course.slope_rating),
    holePars: normalizeHoleValues(course.hole_pars, 4),
    holeHandicaps: normalizeHoleValues(course.hole_handicaps, 1),
  });

  const updateMutation = useMutation({
    mutationFn: async () =>
      client!.updateCourse(leagueId, course.id, {
        name: form.name.trim(),
        par: sumHolePars(form.holePars),
        courseRating: Number(form.courseRating || 35.5),
        slopeRating: Number(form.slopeRating || 113),
        holePars: form.holePars,
        holeHandicaps: form.holeHandicaps,
      }),
    onSuccess: async () => {
      setBanner('success', 'Course updated.');
      await invalidate(['courses', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  return (
    <div className="card course-card">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void updateMutation.mutateAsync();
        }}
      >
        <div className="row">
          <div className="field">
            <label>Name</label>
            <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
          </div>
          <div className="field">
            <label>Total Par</label>
            <div className="scorecard-total">{sumHolePars(form.holePars)}</div>
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label>Course Rating</label>
            <input type="number" step="0.1" value={form.courseRating} onChange={(event) => setForm((current) => ({ ...current, courseRating: event.target.value }))} />
          </div>
          <div className="field">
            <label>Slope Rating</label>
            <input type="number" value={form.slopeRating} onChange={(event) => setForm((current) => ({ ...current, slopeRating: event.target.value }))} />
          </div>
        </div>
        <div className="field">
          <label>Scorecard</label>
          <CourseScorecardGrid
            holePars={form.holePars}
            holeHandicaps={form.holeHandicaps}
            onParChange={(holeIndex, value) =>
              setForm((current) => ({
                ...current,
                holePars: current.holePars.map((par, index) => (index === holeIndex ? value : par)),
              }))
            }
            onHandicapChange={(holeIndex, value) =>
              setForm((current) => ({
                ...current,
                holeHandicaps: current.holeHandicaps.map((handicap, index) => (index === holeIndex ? value : handicap)),
              }))
            }
          />
        </div>
        <div className="inline-actions">
          <button type="submit" disabled={updateMutation.isPending}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
